import {
  AdminApprovalRequestView,
} from '../../../../services/admin-approval-requests/admin-approval-requests.service';
import { AdminPaymentlineView } from '../../../../services/admin-payments/admin-payments.service';
import { AdminSessionAuditView } from '../../../../services/admin-sessions/admin-sessions.service';
import { ReportView } from '../../../../services/reports/reports.service';
import {
  BarRow,
  CancellationTotals,
  OrdersSummary,
  PaymentByMethod,
  SameDaysComparison,
  TotalsCard,
  TotalsMode,
  WaiterActionBreakdownRow,
} from '../types/dashboard-page.types';

// Wrapped in LRE/PDF so "123.4 ₪" always renders number-then-symbol, even inside an RTL ancestor.
export const asCurrency = (value: number): string => `‪${new Intl.NumberFormat('he-IL', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
}).format(value)} ₪‬`;

export const asDate = (iso: string): string => new Date(iso).toLocaleDateString('he-IL');

export const asHour = (iso: string): string => new Date(iso).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });

export const sum = (values: number[]): number => values.reduce((acc, value) => acc + value, 0);

export const computeSameDaysComparison = (
  anchorSession: AdminSessionAuditView | null,
  allSessions: AdminSessionAuditView[],
): SameDaysComparison => {
  if (!anchorSession) {
    return null;
  }
  const anchorDate = new Date(anchorSession.openTime);
  const weekday = anchorDate.getDay();

  const lastMonthCandidates = allSessions.filter((row) => {
    const day = new Date(row.openTime);
    const diffDays = Math.round((anchorDate.getTime() - day.getTime()) / (1000 * 60 * 60 * 24));
    return diffDays >= 25 && diffDays <= 40 && day.getDay() === weekday;
  });

  if (lastMonthCandidates.length > 0) {
    const baseline = sum(lastMonthCandidates.map((row) => row.salesTotal)) / lastMonthCandidates.length;
    return { label: 'sameMonth', baseline };
  }

  const lastWeekCandidates = allSessions.filter((row) => {
    const day = new Date(row.openTime);
    const diffDays = Math.round((anchorDate.getTime() - day.getTime()) / (1000 * 60 * 60 * 24));
    return diffDays >= 6 && diffDays <= 8 && day.getDay() === weekday;
  });

  if (lastWeekCandidates.length > 0) {
    const baseline = sum(lastWeekCandidates.map((row) => row.salesTotal)) / lastWeekCandidates.length;
    return { label: 'sameWeek', baseline };
  }

  return null;
};

export const computeWaiterActionBreakdown = (rows: AdminApprovalRequestView[]): WaiterActionBreakdownRow[] => {
  const byWaiter = new Map<string, Omit<WaiterActionBreakdownRow, 'name' | 'totalCost'>>();
  rows
    .filter((row) => row.status !== 'pending')
    .forEach((row) => {
      const entry = byWaiter.get(row.requestedByName) ?? {
        onTheHouse: { count: 0, cost: 0 },
        cancel: { count: 0, cost: 0 },
        itemDiscount: { count: 0, cost: 0 },
        globalDiscount: { count: 0, cost: 0 },
      };
      if (row.actionType === 'on_the_house') {
        entry.onTheHouse.count += 1;
        entry.onTheHouse.cost += row.cost;
      } else if (row.actionType === 'cancel') {
        entry.cancel.count += 1;
        entry.cancel.cost += row.cost;
      } else if (row.actionType === 'item_discount') {
        entry.itemDiscount.count += 1;
        entry.itemDiscount.cost += row.cost;
      } else if (row.actionType === 'global_discount') {
        entry.globalDiscount.count += 1;
        entry.globalDiscount.cost += row.cost;
      }
      byWaiter.set(row.requestedByName, entry);
    });
  return [...byWaiter.entries()]
    .map(([name, breakdown]) => ({
      name,
      ...breakdown,
      totalCost: breakdown.onTheHouse.cost + breakdown.cancel.cost + breakdown.itemDiscount.cost + breakdown.globalDiscount.cost,
    }))
    .sort((a, b) => b.totalCost - a.totalCost);
};

// `baseAmount` = tip-free sale; `amount` = tip-inclusive charge; `globalTipCollected` = the tip.
export const sumDonePaymentsByMethod = (
  payments: AdminPaymentlineView[],
  method: AdminPaymentlineView['method'],
  field: 'amount' | 'baseAmount' | 'globalTipCollected',
): number => sum(
  payments
    .filter((row) => row.status === 'done' && row.method === method)
    .map((row) => {
      if (field === 'globalTipCollected') return row.globalTipCollected ?? 0;
      return field === 'baseAmount' ? row.baseAmount : row.amount;
    }),
);

export const computePaymentByMethod = (payments: AdminPaymentlineView[], report: ReportView | null): PaymentByMethod => {
  if (payments.length === 0 && report) {
    return {
      cash: report.paymentsCash,
      credit: report.paymentsCredit,
      tipsCash: report.tipsCash,
      tipsCredit: report.tipsCredit,
    };
  }

  return {
    cash: sumDonePaymentsByMethod(payments, 'cash', 'baseAmount'),
    credit: sumDonePaymentsByMethod(payments, 'credit', 'baseAmount'),
    tipsCash: report?.tipsCash ?? 0,
    tipsCredit: report?.tipsCredit ?? 0,
  };
};

export const computeGraphRows = (payments: AdminPaymentlineView[]): BarRow[] => {
  const doneRows = payments
    .filter((row) => row.status === 'done' && row.baseAmount > 0)
    .sort((a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime());

  if (doneRows.length === 0) {
    return [];
  }

  const bucket = new Map<string, number>();
  doneRows.forEach((row) => {
    const d = new Date(row.updatedAt);
    const key = `${String(d.getHours()).padStart(2, '0')}:00`;
    bucket.set(key, (bucket.get(key) ?? 0) + row.baseAmount);
  });

  return [...bucket.entries()]
    .map(([label, value]) => ({ label, value }))
    .slice(-7);
};

export const computeClosedOrdersSummary = (payments: AdminPaymentlineView[], report: ReportView | null): OrdersSummary => {
  const closedDone = payments.filter((row) => row.orderStatus === 'closed' && row.status === 'done');
  const uniqueOrders = new Set(closedDone.map((row) => row.orderCode));
  const amountFromPayments = sum(closedDone.map((row) => row.baseAmount));
  return {
    count: uniqueOrders.size,
    amount: uniqueOrders.size > 0 ? amountFromPayments : (report?.closedRevenue ?? amountFromPayments),
  };
};

export const computeOpenOrdersSummary = (payments: AdminPaymentlineView[], report: ReportView | null): OrdersSummary => {
  if (report) {
    return { count: report.openOrdersCount, amount: report.openOrdersTotal };
  }
  const openDone = payments.filter((row) => row.orderStatus === 'open' && row.status === 'done');
  const uniqueOrders = new Set(openDone.map((row) => row.orderCode));
  return { count: uniqueOrders.size, amount: sum(openDone.map((row) => row.baseAmount)) };
};

export const computeCancellationTotals = (
  cancelDetails: { orderlines: { unitPrice: number; quantity: number }[] }[],
  report: ReportView | null,
): CancellationTotals => {
  const fullAmount = sum(
    cancelDetails.flatMap((detail) => detail.orderlines.map((line) => line.unitPrice * line.quantity)),
  );
  const totalItems = sum(
    cancelDetails.flatMap((detail) => detail.orderlines.map((line) => line.quantity)),
  );
  return {
    itemsCount: totalItems > 0 ? totalItems : (report?.canceledItemsCount ?? 0),
    itemsAmount: fullAmount > 0 ? fullAmount : (report?.canceledItemsAmount ?? 0),
    orderCount: report?.canceledOrdersCount ?? 0,
  };
};

export const computeTotalsCards = (
  isOpen: boolean,
  closedOrdersSummary: OrdersSummary,
  openOrdersSummary: OrdersSummary,
  closedShiftValue: number,
): TotalsCard[] => (isOpen
  ? [
    { key: 'ordersTotal' as TotalsMode, title: 'סה״כ', value: closedOrdersSummary.amount + openOrdersSummary.amount, count: closedOrdersSummary.count + openOrdersSummary.count },
    { key: 'closedOrders' as TotalsMode, title: 'הזמנות סגורות', value: closedOrdersSummary.amount, count: closedOrdersSummary.count },
    { key: 'openOrders' as TotalsMode, title: 'הזמנות פתוחות', value: openOrdersSummary.amount, count: openOrdersSummary.count },
  ]
  : [
    { key: 'closedShift' as TotalsMode, title: 'סה״כ', value: closedShiftValue },
  ]);

export const formatPct = (value: number | null): string => {
  if (value === null || !Number.isFinite(value)) {
    return 'n/a';
  }
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
};

export const normalizeTenantToken = (value: string): string => value.toLowerCase().replace(/[^a-z0-9]/g, '');

export const isSameTenant = (candidateTenantId: string, routeTenantId: string): boolean => {
  if (candidateTenantId === routeTenantId) {
    return true;
  }
  const candidate = normalizeTenantToken(candidateTenantId);
  const route = normalizeTenantToken(routeTenantId);
  return candidate.length > 0 && route.length > 0 && (candidate.includes(route) || route.includes(candidate));
};

// Mirrors backend calculateOrderDiscount (apps/backend/src/modules/receipt/receipt-totals.util.ts):
// discountMode 'percentage' resolves against the net items total (after per-line discounts), clamped to it.
export const computeGlobalDiscountAmount = (
  netItemsTotal: number,
  discountMode: 'amount' | 'percentage' | null,
  discountInput: number | null,
): number => {
  if (!(netItemsTotal > 0) || !discountMode || discountInput == null) {
    return 0;
  }
  const raw = discountMode === 'amount' ? discountInput : (netItemsTotal * discountInput) / 100;
  return Math.round(Math.min(raw, netItemsTotal) * 10) / 10;
};

export const doesTenantAliasMatch = (value: string, aliases: string[]): boolean => {
  const normalizedValue = normalizeTenantToken(value);
  if (!normalizedValue) {
    return false;
  }
  return aliases.some((alias) => alias.includes(normalizedValue) || normalizedValue.includes(alias));
};
